/**
 * Serializes a list-page filter object into the query string for an API
 * request. This is the ONLY place filter values become a query string — the
 * browser's address bar never receives them (see useManagementTableState).
 *
 * Rules, matching what the backend Query DTOs parse:
 *  - arrays        → comma-joined (`category=Laptop,Monitor`); the DTO's
 *                    toStringArray/toNumberArray splits on the same comma
 *  - booleans      → 'true' / 'false'
 *  - numbers       → plain decimal; NaN/Infinity are dropped, never sent as
 *                    the literal "NaN" (which the DTO's @IsNumber rejects
 *                    with a 400, blanking the whole list)
 *  - Date          → ISO yyyy-mm-dd, so date ranges compare consistently
 *  - dropped       → undefined, null, '', [], and array entries that are
 *                    themselves empty/nullish
 *
 * Everything is passed through URLSearchParams, so values are percent-encoded
 * rather than interpolated into a URL by hand.
 */

const toScalar = (value: unknown): string | undefined => {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'string') return value === '' ? undefined : value;
    if (typeof value === 'number') return Number.isFinite(value) ? String(value) : undefined;
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? undefined : value.toISOString().slice(0, 10);
    }
    return String(value);
};

export function buildListParams(filters: Record<string, any> = {}): URLSearchParams {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            const parts = value
                .map(toScalar)
                .filter((v): v is string => v !== undefined);
            if (parts.length) params.set(key, parts.join(','));
            return;
        }

        const scalar = toScalar(value);
        if (scalar !== undefined) params.set(key, scalar);
    });

    return params;
}
